import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import type { Invoice, Order } from "@/api.types.v2";
import * as z from "zod";

// Types
interface InvoicesResponse {
  data: Invoice[];
  metadata: {
    total: number;
    page: number;
    totalPages: number;
  };
}

export interface InvoiceDetail extends Invoice {
  invoiceOrders: {
    id: string;
    order: {
      id: string;
      orderNumber: number;
      balanceCents: number | null;
      totalCents: number | null;
      shipments: { id: string; shipmentNumber: number }[];
    };
  }[];
}

// Schema
export const createInvoiceSchema = z.object({
  customerId: z.string().nonempty("Customer is required"),
  orderIds: z
    .array(z.string())
    .min(1, "At least one order must be selected")
    .max(
      500,
      "Cannot create invoice with more than 500 orders. Please select fewer orders or split into multiple invoices."
    ),
  dueDate: z.date(),
  discount: z
    .object({
      type: z.enum(["percentage", "amount"]),
      value: z.number().min(0).max(100000),
    })
    .optional(),
});

export type CreateInvoiceSchema = z.infer<typeof createInvoiceSchema>;

// Get invoices with pagination
export function useInvoices(page: number, limit: number) {
  const { token } = useSession();

  return useQuery<InvoicesResponse>({
    queryKey: ["invoices", page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const { data } = await axios.get<InvoicesResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

export function useInvoiceById(invoiceId: string | null) {
  const { token } = useSession();

  return useQuery<InvoiceDetail>({
    queryKey: ["invoice", invoiceId],
    queryFn: async () => {
      const { data } = await axios.get<InvoiceDetail>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices/${invoiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token && !!invoiceId,
  });
}

// Get customer orders for invoice creation
export function useCustomerOrders(
  customerId: string | undefined,
  dateRange: { from: Date; to: Date } | undefined,
  page = 1,
  limit = 100
) {
  const { token } = useSession();

  return useQuery<{
    data: Order[];
    metadata: { totalCount: number; totalPages: number };
  }>({
    queryKey: [
      "customerOrders",
      customerId,
      dateRange?.from,
      dateRange?.to,
      page,
      limit,
    ],
    queryFn: async () => {
      if (!dateRange?.from || !dateRange?.to)
        return { data: [], metadata: { totalCount: 0, totalPages: 0 } };

      const fromDate = new Date(dateRange.from);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);

      const { data } = await axios.get<{
        data: Order[];
        metadata: { totalCount: number; totalPages: number };
      }>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/orders`,
        {
          params: {
            from: fromDate.toISOString(),
            to: toDate.toISOString(),
            invoiceable: true,
            type: "all",
            page,
            limit,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token && !!customerId && !!dateRange?.from && !!dateRange?.to,
  });
}

// Create invoice mutation
export function useCreateInvoice() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<Invoice, Error, CreateInvoiceSchema>({
    mutationFn: async (values) => {
      // UUID per logical click. Retry of the same click reuses it so Stripe
      // dedups; a deliberate re-submit from a fresh form gets a fresh UUID.
      const idempotencyKey = crypto.randomUUID();
      const { data } = await axios.post<Invoice>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Idempotency-Key": idempotencyKey,
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}

// Generate draft invoice for an order
export function useGenerateDraftInvoice() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; data: Invoice },
    Error,
    { orderId: string }
  >({
    mutationFn: async ({ orderId }) => {
      const { data } = await axios.post<{ success: boolean; data: Invoice }>(
        `${import.meta.env.VITE_API_URL}/api/v2/orders/${orderId}/generate-draft-invoice`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

// Send draft invoice to Stripe
export function useSendDraftInvoice() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; data: Invoice },
    Error,
    { invoiceId: string }
  >({
    mutationFn: async ({ invoiceId }) => {
      const { data } = await axios.post<{ success: boolean; data: Invoice }>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices/${invoiceId}/send`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      await queryClient.invalidateQueries({ queryKey: ["invoice"] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["orderPaymentStatus"] });
    },
  });
}

// Types for available orders
interface AvailableOrder {
  id: string;
  orderNumber: number;
  createdAt: string;
  totalCents: number;
  subtotalCents: number;
  taxCents: number;
  shipments: {
    id: string;
    status: string;
    serviceCompletedAt: string;
    addresses: {
      contactName: string;
      address: {
        city: string;
        province: string;
      };
    }[];
  }[];
}

interface AvailableOrdersResponse {
  data: AvailableOrder[];
  metadata: {
    count: number;
    invoiceId: string;
  };
}

// Get orders available to add to a DRAFT invoice
export function useAvailableOrdersForInvoice(invoiceId: string | null) {
  const { token } = useSession();

  return useQuery<AvailableOrdersResponse>({
    queryKey: ["availableOrdersForInvoice", invoiceId],
    queryFn: async () => {
      const { data } = await axios.get<AvailableOrdersResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices/${invoiceId}/available-orders`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token && !!invoiceId,
  });
}

// Add orders to a DRAFT invoice
export function useAddOrdersToInvoice() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; addedCount: number },
    Error,
    { invoiceId: string; orderIds: string[] }
  >({
    mutationFn: async ({ invoiceId, orderIds }) => {
      const { data } = await axios.post<{
        success: boolean;
        addedCount: number;
      }>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices/${invoiceId}/add-orders`,
        { orderIds },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      await queryClient.invalidateQueries({
        queryKey: ["availableOrdersForInvoice"],
      });
    },
  });
}

// Types for stuck orders
interface StuckCustomer {
  customerId: string;
  customerName: string;
  pendingOrderCount: number;
  completedOrderCount: number;
  oldestOrderDate: string;
  daysSinceOldestOrder: number;
  paymentTerms: {
    code: string;
    dayInterval: number;
  };
  reason: string;
  canInvoice: boolean;
  blockedReason: string | null;
}

interface StuckOrdersResponse {
  stuckCount: number;
  customers: StuckCustomer[];
}

// Get stuck orders (orders that should be invoiced but aren't)
export function useStuckOrders() {
  const { token } = useSession();

  return useQuery<StuckOrdersResponse>({
    queryKey: ["stuckOrders"],
    queryFn: async () => {
      const { data } = await axios.get<StuckOrdersResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices/stuck-orders`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Process pending invoices
export function useProcessPendingInvoices() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    {
      success: boolean;
      processed: number;
      invoicesGenerated: number;
      results: {
        customerId: string;
        invoiceGenerated: boolean;
        invoiceId?: string | null;
        error?: string;
      }[];
    },
    Error,
    { customerId?: string }
  >({
    mutationFn: async ({ customerId }) => {
      const { data } = await axios.post<{
        success: boolean;
        processed: number;
        invoicesGenerated: number;
        results: {
          customerId: string;
          invoiceGenerated: boolean;
          invoiceId?: string | null;
          error?: string;
        }[];
      }>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices/process-pending`,
        { customerId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["stuckOrders"] });
      await queryClient.invalidateQueries({ queryKey: ["upcomingInvoices"] });
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}

// Types for upcoming invoices
interface UpcomingInvoice {
  customerId: string;
  paymentTermsId: string;
  customerName: string;
  paymentTerms: {
    code: string;
    dayInterval: number;
  };
  orderCount: number;
  daysSinceLastInvoice: number;
  intervalProgress: number;
  overallProgress: number;
  estimatedTrigger: string;
  totalPendingCents: number;
  completedOrderCount: number;
  completedTotalCents: number;
  oldestOrderDate: string;
}

interface UpcomingInvoicesResponse {
  count: number;
  customers: UpcomingInvoice[];
  metadata: {
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Get upcoming invoices (customers approaching invoice interval)
export function useUpcomingInvoices(page: number) {
  const { token } = useSession();

  return useQuery<UpcomingInvoicesResponse>({
    queryKey: ["upcomingInvoices", page],
    queryFn: async () => {
      const { data } = await axios.get<UpcomingInvoicesResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/invoices/upcoming`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}
